import { MenuItem, Tooltip } from '@mui/material';
import cx from 'classnames';
import moment from 'moment-timezone';
import { PureComponent } from 'react';
import { ConnectedProps, connect } from 'react-redux';

import { ExpertRequestType } from '@/__generated__/graphql';
import FAIcon from '@/components/Icon/FAIcon';
import MaterialIcon from '@/components/Icon/MaterialIcon';
import IconMenu from '@/components/IconMenu';
import Link from '@/components/Link';
import { ErStageBadge } from '@/expertrequest/components/ErStageBadge';
import { isDeprecatedType } from '@/expertrequest/store';
import { isEngagementsEnabledForGroup } from '@/features/consulting-engagements/gate';
import { RootState } from '@/store';
import { formatDateTime } from '@/utils';

import EditAssociatedProjectDialog from './EditAssociatedProjectDialog';
import s from './ExpertRequestPreview.module.scss';

interface ExpertRequestPreviewProps {
  className?: string;
  request: any;
  project?: any;
  showState?: boolean;
  showContextActions?: boolean;
  showAddAction?: boolean;
}

const connector = connect((state: RootState) => ({
  viewer: state.viewer,
}));

class ExpertRequestPreview extends PureComponent<
  ExpertRequestPreviewProps & ConnectedProps<typeof connector>
> {
  state = {
    editProjectOpen: false,
  };

  openEditProject = () => this.setState({ editProjectOpen: true });

  closeEditProject = () => this.setState({ editProjectOpen: false });

  render() {
    const { viewer, className, request, project, showState, showContextActions, showAddAction } =
      this.props;

    return (
      <div className={cx(className)}>
        <div className={s.requestCard}>
          <div className={s.requestTitle}>
            <Link
              to={request.html_url}
              className={s.requestTitleLink}
              id={`expertRequest-${request.id}`}
            >
              <FAIcon
                icon="user-circle-o"
                style={{ width: 16, height: 16, marginRight: 6, opacity: 0.6 }}
              />
              <span>{request.name}</span>
            </Link>

            <Tooltip
              title={formatDateTime(request.created_at, viewer.timezone || undefined)}
              placement="top"
            >
              <div className={s.requestTitleAge}>
                Created {moment(request.created_at).fromNow()}
              </div>
            </Tooltip>
          </div>
          <div className={s.requestDetails}>
            <div className={s.requestStats}>
              <Link to={`${request.html_url}?section=experts`} className={s.stat}>
                <MaterialIcon icon="people" className={s.statIcon} />
                <span
                  className={request.stats.candidates_count > 0 ? s.statCount : s.statCountZero}
                >
                  {request.stats.candidates_count}
                </span>
                <span className={s.statLabel}>Matches</span>
              </Link>
              <Link to={`${request.html_url}?section=consultations`} className={s.stat}>
                <MaterialIcon icon="phone_in_talk" className={s.statIcon} />
                <span className={request.stats.calls > 0 ? s.statCount : s.statCountZero}>
                  {request.stats.calls}
                </span>
                <span className={s.statLabel}>Calls</span>
              </Link>
            </div>
            <div className={s.requestControls}>
              {showState && (
                <ErStageBadge stage={request.stage} state={request.state} size="small" />
              )}
              {showContextActions && request.permissions && (
                <IconMenu buttonStyle={{ marginLeft: 4 }}>
                  {project && showAddAction && (
                    <Link to={`/request_expert?project_id=${project.id}`} menu>
                      <MenuItem key="addExpertRequest">Add Expert Request</MenuItem>
                    </Link>
                  )}

                  {project && request.permissions.includes('update') && (
                    <MenuItem key="editProject" onClick={this.openEditProject}>
                      Edit Associated Project
                    </MenuItem>
                  )}
                  {/* ENG-5670: Make a Copy lands on step 2 of the wizard and skips the
                      type picker, copying er_type verbatim, so it is a second door into
                      a type the account may not have. The api refuses the create either
                      way; hiding the affordance means the user does not reach that error.
                      Keyed on the ER's own project group, matching the api's check. */}
                  {!isDeprecatedType(request.er_type) &&
                    !(
                      request.er_type === ExpertRequestType.ConsultingProject &&
                      isEngagementsEnabledForGroup(project?.group)
                    ) && (
                      <Link to={`/request_expert/details?copy_from=${request.id}`} menu>
                        <MenuItem key="makeACopy">Make a Copy</MenuItem>
                      </Link>
                    )}
                </IconMenu>
              )}
            </div>
          </div>
        </div>
        {project && this.state.editProjectOpen && (
          <EditAssociatedProjectDialog
            open
            onClose={this.closeEditProject}
            expertRequestId={request.id}
            currentProjectId={project.id}
            currentGroupId={project.group?.id}
          />
        )}
      </div>
    );
  }
}

export default connector(ExpertRequestPreview);
